<template>
  <div class="min-h-screen bg-dark flex">
    <!-- Sidebar -->
    <aside
      v-if="userStore.hasOrg"
      :class="[
        'fixed inset-y-0 left-0 z-50 bg-dark-secondary border-r border-gray-700 transform transition-all duration-300 ease-in-out',
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0',
        isCollapsed ? 'w-16' : 'w-64'
      ]"
    >
      <!-- Logo and Collapse Button -->
      <div class="h-16 flex items-center justify-between px-4 border-b border-gray-700">
        <img v-if="!isCollapsed" class="h-8 w-auto" src="@/assets/logo.png" alt="Search Plus" />
        <img v-else class="h-8 w-8" src="@/assets/logo.png" alt="Search Plus" />
        
        <!-- Collapse Toggle Button -->
        <button
          @click="isCollapsed = !isCollapsed"
          class="hidden sm:flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
        >
          <svg
            class="h-4 w-4 transform transition-transform duration-300"
            :class="{ 'rotate-180': isCollapsed }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
          </svg>
        </button>
      </div>

      <!-- Org Switcher -->
      <div class="px-2 pt-4 pb-2 border-b border-gray-700 org-switcher-container">
        <div class="relative">
          <button
            @click="isOrgDropdownOpen = !isOrgDropdownOpen"
            class="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:bg-gray-700 transition-colors duration-150 group"
          >
            <div class="flex items-center min-w-0 flex-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5 mr-2 flex-shrink-0"
                :class="{ 'mr-0': isCollapsed }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              <span
                v-if="!isCollapsed"
                class="truncate text-left"
                :title="(orgStore.orgData as any)?.org_name || orgStore.orgData?.name || orgStore.orgData?.id || 'Organization'"
              >
                {{ (orgStore.orgData as any)?.org_name || orgStore.orgData?.name || orgStore.orgData?.id || 'Organization' }}
              </span>
            </div>
            <svg
              v-if="!isCollapsed"
              class="w-4 h-4 ml-2 flex-shrink-0 transition-transform duration-200"
              :class="{ 'rotate-180': isOrgDropdownOpen }"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
            <!-- Tooltip for collapsed state -->
            <div
              v-if="isCollapsed"
              class="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap z-50"
            >
             {{ (orgStore.orgData as any)?.org_name || orgStore.orgData?.name || orgStore.orgData?.id || 'Organization' }}
            </div>
          </button>

          <!-- Dropdown Menu -->
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="isOrgDropdownOpen && !isCollapsed"
              class="absolute z-50 mt-1 w-full bg-dark-secondary border border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto"
            >
              <div class="py-1">
                <div
                  v-for="userOrg in orgStore.userOrgs"
                  :key="userOrg.org_id"
                  @click="handleSwitchOrg(userOrg.org_id)"
                  class="px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 cursor-pointer flex items-center justify-between"
                  :class="{
                    'bg-gray-700/50': userOrg.org_id === orgStore.orgData?.id
                  }"
                >
                  <span class="truncate">{{ userOrg.org_name }}</span>
                  <svg
                    v-if="userOrg.org_id === orgStore.orgData?.id"
                    class="w-4 h-4 ml-2 text-primary flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <div
                  v-if="orgStore.userOrgs.length === 0 && !orgStore.loadingOrgs"
                  class="px-3 py-2 text-sm text-gray-500"
                >
                  No organizations
                </div>
                <div
                  v-if="orgStore.loadingOrgs"
                  class="px-3 py-2 text-sm text-gray-500"
                >
                  Loading...
                </div>
                <!-- Action Buttons -->
                <div class="border-t border-gray-700 mt-1 pt-1 space-y-1">
                  <button
                    @click.stop="openCreateOrgModal"
                    class="w-full px-3 py-2 text-sm text-white hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    <span>Create organisation</span>
                  </button>
                  <button
                    @click.stop="openJoinOrgModal"
                    class="w-full px-3 py-2 text-sm text-white hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                      />
                    </svg>
                    <span>Join organisation</span>
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Navigation Links -->
      <nav class="mt-4 px-2 space-y-1">
        <router-link
          v-for="item in navigation"
          :key="item.name"
          :to="item.href"
          class="flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 group"
          :class="[
            $route.path === item.href
              ? 'bg-gray-700'
              : 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
          ]"
          @click="isMobileMenuOpen = false"
        >
          <component :is="item.icon" class="w-5 h-5" :class="{ 'mr-3': !isCollapsed }" />
          <span :class="{ 'opacity-0 w-0 overflow-hidden': isCollapsed }">
            {{ item.name }}
          </span>
          <!-- Tooltip for collapsed state -->
          <div
            v-if="isCollapsed"
            class="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap"
          >
            {{ item.name }}
          </div>
        </router-link>
      </nav>

      <!-- Release Notes Panel -->
      <div class="absolute bottom-[68px] w-full">
        <!-- Button integrated with drawer -->
        <button
          @click="isReleaseNotesOpen = !isReleaseNotesOpen"
          class="w-full flex items-center px-4 py-3 text-sm font-medium border-t border-b border-gray-700/50 text-gray-400 hover:bg-gray-700/50 hover:text-gray-300 transition-all duration-200 group relative"
          :class="{ 'bg-gray-700/50': isReleaseNotesOpen }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" 
              class="w-5 h-5" 
              :class="{ 'mr-3': !isCollapsed }" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span v-if="!isCollapsed" class="whitespace-nowrap">
            What's New
          </span>
          <div class="flex-1"></div>
          <div 
            v-if="hasUnreadUpdates"
            class="h-2 w-2 rounded-full bg-green-500 ring-2 ring-green-500 ring-opacity-50 animate-pulse ml-2"
          ></div>
          <!-- Tooltip for collapsed state -->
          <div
            v-if="isCollapsed"
            class="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap"
          >
            What's New
          </div>
        </button>

        <!-- Release Notes Content -->
        <transition
          enter-active-class="transition-all duration-300 ease-out"
          leave-active-class="transition-all duration-200 ease-in"
          enter-from-class="opacity-0 -translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-2"
        >
          <div
            v-if="isReleaseNotesOpen"
            class="bg-gray-800/40 backdrop-blur-sm border-b border-gray-700/50"
          >
            <div class="py-3 px-4 space-y-4 text-sm">
              <!-- Timeline Track -->
              <div class="relative space-y-4">
                <!-- Version 1.4.0 -->
                <div class="relative pl-4 group">
                  <!-- Timeline dot and line -->
                  <div class="absolute left-0 top-[9px] flex items-center justify-center">
                    <div class="h-2 w-2 rounded-full bg-primary group-hover:scale-150 transition-transform duration-300"></div>
                    <div class="absolute h-[calc(100%+16px)] w-[1px] bg-gray-700/50 top-2 left-[3px]"></div>
                  </div>
                  
                  <!-- Content -->
                  <div>
                    <div class="flex items-center gap-2 text-primary">
                      <span class="font-medium">v1.4.0</span>
                      <span class="text-[11px] text-gray-500">Sept 16, 2025</span>
                      <span class="px-1.5 py-0.5 text-[10px] font-medium bg-primary/10 text-primary rounded-full">Latest</span>
                    </div>
                    <ul class="mt-1 space-y-1 text-gray-400 text-xs">
                      <li class="flex items-center gap-1.5 group/item hover:text-gray-300 transition-colors">
                        <span class="h-0.5 w-0.5 rounded-full bg-primary/50 group-hover/item:bg-primary transition-colors"></span>
                        <span>Enhanced AI insights with improved accuracy and data reliability</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Version 1.3.0 -->
                <div class="relative pl-4 group">
                  <!-- Timeline dot and line -->
                  <div class="absolute left-0 top-[9px] flex items-center justify-center">
                    <div class="h-2 w-2 rounded-full bg-gray-600 group-hover:scale-150 transition-transform duration-300"></div>
                    <div class="absolute h-[calc(100%+16px)] w-[1px] bg-gray-700/50 top-2 left-[3px]"></div>
                  </div>
                  
                  <!-- Content -->
                  <div>
                    <div class="flex items-center gap-2 text-gray-400">
                      <span class="font-medium">v1.3.0</span>
                      <span class="text-[11px] text-gray-500">Sept 15, 2025</span>
                    </div>
                    <ul class="mt-1 space-y-1 text-gray-500 text-xs">
                      <li class="flex items-center gap-1.5 group/item hover:text-gray-400 transition-colors">
                        <span class="h-0.5 w-0.5 rounded-full bg-gray-600 group-hover/item:bg-gray-500 transition-colors"></span>
                        <span>Improved Shopify product sync with draft/archived status</span>
                      </li>
                      <li class="flex items-center gap-1.5 group/item hover:text-gray-400 transition-colors">
                        <span class="h-0.5 w-0.5 rounded-full bg-gray-600 group-hover/item:bg-gray-500 transition-colors"></span>
                        <span>Added visual indicators for product status</span>
                      </li>
                      <li class="flex items-center gap-1.5 group/item hover:text-gray-400 transition-colors">
                        <span class="h-0.5 w-0.5 rounded-full bg-gray-600 group-hover/item:bg-gray-500 transition-colors"></span>
                        <span>Enhanced product management with helpful tooltips</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Version 1.2.0 -->
                <div class="relative pl-4 group">
                  <!-- Timeline dot -->
                  <div class="absolute left-0 top-[9px] flex items-center justify-center">
                    <div class="h-2 w-2 rounded-full bg-gray-600 group-hover:scale-150 transition-transform duration-300"></div>
                  </div>
                  
                  <!-- Content -->
                  <div>
                    <div class="flex items-center gap-2 text-gray-400">
                      <span class="font-medium">v1.2.0</span>
                      <span class="text-[11px] text-gray-500">Sept 4, 2025</span>
                    </div>
                    <ul class="mt-1 space-y-1 text-gray-500 text-xs">
                      <li class="flex items-center gap-1.5 group/item hover:text-gray-400 transition-colors">
                        <span class="h-0.5 w-0.5 rounded-full bg-gray-600 group-hover/item:bg-gray-500 transition-colors"></span>
                        <span>Added spam detection for searches</span>
                      </li>
                      <li class="flex items-center gap-1.5 group/item hover:text-gray-400 transition-colors">
                        <span class="h-0.5 w-0.5 rounded-full bg-gray-600 group-hover/item:bg-gray-500 transition-colors"></span>
                        <span>Improved search analytics</span>
                      </li>
                      <li class="flex items-center gap-1.5 group/item hover:text-gray-400 transition-colors">
                        <span class="h-0.5 w-0.5 rounded-full bg-gray-600 group-hover/item:bg-gray-500 transition-colors"></span>
                        <span>New customization options</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Sign Out Button -->
      <div class="absolute bottom-0 w-full px-2 py-4">
        <button
          @click="handleSignOut"
          class="w-full flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-400 hover:bg-gray-700 hover:text-gray-300 transition-colors duration-150 group"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" :class="{ 'mr-3': !isCollapsed }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span :class="{ 'opacity-0 w-0 overflow-hidden': isCollapsed }">
            Sign Out
          </span>
          <!-- Tooltip for collapsed state -->
          <div
            v-if="isCollapsed"
            class="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap"
          >
            Sign Out
          </div>
        </button>
      </div>
    </aside>

    <!-- Mobile menu button -->
    <div class="sm:hidden fixed top-0 left-0 z-50 m-4">
      <button
        @click="isMobileMenuOpen = !isMobileMenuOpen"
        type="button"
        class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary bg-dark-secondary"
      >
        <span class="sr-only">Open main menu</span>
        <svg
          v-if="!isMobileMenuOpen"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
        <svg
          v-else
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Overlay -->
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 bg-black bg-opacity-50 sm:hidden z-40"
      @click="isMobileMenuOpen = false"
    ></div>

    <!-- Main Content -->
    <div class="flex-1 transition-all duration-300 ease-in-out" :class="{ 'sm:ml-64': !isCollapsed, 'sm:ml-16': isCollapsed }">
      <main class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <slot></slot>
        </div>
      </main>
    </div>

    <!-- Join Organization Modal - Rendered at root level -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isJoinOrgModalOpen"
        class="fixed inset-0 z-[9999] overflow-y-auto"
        @click.self="closeJoinOrgModal"
      >
        <!-- Background overlay -->
        <div
          class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          @click="closeJoinOrgModal"
        ></div>

        <!-- Modal container - centered -->
        <div class="flex items-center justify-center min-h-screen px-4 py-4">
          <!-- Modal panel -->
          <div
            class="relative bg-dark-secondary rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg"
            @click.stop
          >
            <div class="bg-dark-secondary px-4 pt-5 pb-4 sm:p-6">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-medium text-gray-100">Join Organisation</h3>
                <button
                  @click="closeJoinOrgModal"
                  class="text-gray-400 hover:text-gray-300"
                >
                  <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form @submit.prevent="handleJoinOrg" class="space-y-4">
                <div>
                  <label for="org-id" class="block text-sm font-medium text-gray-300 mb-1">
                    Organization ID
                  </label>
                  <input
                    id="org-id"
                    v-model="joinOrgForm.orgId"
                    type="text"
                    required
                    class="w-full px-3 py-2 bg-dark border border-gray-700 rounded-md text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter organization ID"
                    :disabled="isJoiningOrg"
                  />
                  <p class="mt-1 text-xs text-gray-500">Ask your team admin for this ID</p>
                </div>

                <div>
                  <label for="share-code" class="block text-sm font-medium text-gray-300 mb-1">
                    Share Code
                  </label>
                  <input
                    id="share-code"
                    v-model="joinOrgForm.shareCode"
                    type="text"
                    required
                    class="w-full px-3 py-2 bg-dark border border-gray-700 rounded-md text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter share code"
                    :disabled="isJoiningOrg"
                  />
                  <p class="mt-1 text-xs text-gray-500">Ask your team admin for the share code</p>
                </div>

                <div v-if="joinOrgError" class="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                  <p class="text-sm text-red-400">{{ joinOrgError }}</p>
                </div>

                <div class="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    @click="closeJoinOrgModal"
                    class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
                    :disabled="isJoiningOrg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="isJoiningOrg"
                  >
                    {{ isJoiningOrg ? 'Joining...' : 'Join Organisation' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Create Organization Modal -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isCreateOrgModalOpen"
        class="fixed inset-0 z-[9999] overflow-y-auto"
        @click.self="closeCreateOrgModal"
      >
        <!-- Background overlay -->
        <div
          class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          @click="closeCreateOrgModal"
        ></div>

        <!-- Modal container - centered -->
        <div class="flex items-center justify-center min-h-screen px-4 py-4">
          <!-- Modal panel -->
          <div
            class="relative bg-dark-secondary rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg"
            @click.stop
          >
            <div class="bg-dark-secondary px-4 pt-5 pb-4 sm:p-6">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-medium text-gray-100">Create Organisation</h3>
                <button
                  @click="closeCreateOrgModal"
                  class="text-gray-400 hover:text-gray-300"
                >
                  <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form @submit.prevent="handleCreateOrg" class="space-y-4">
                <div>
                  <label for="org-name" class="block text-sm font-medium text-gray-300 mb-1">
                    Organisation Name
                  </label>
                  <input
                    id="org-name"
                    v-model="createOrgForm.orgName"
                    type="text"
                    required
                    class="w-full px-3 py-2 bg-dark border border-gray-700 rounded-md text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter organisation name"
                    :disabled="isCreatingOrg"
                  />
                  <p class="mt-1 text-xs text-gray-500">Choose a name for your organisation</p>
                </div>

                <div v-if="createOrgError" class="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                  <p class="text-sm text-red-400">{{ createOrgError }}</p>
                </div>

                <div class="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    @click="closeCreateOrgModal"
                    class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
                    :disabled="isCreatingOrg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="isCreatingOrg"
                  >
                    {{ isCreatingOrg ? 'Creating...' : 'Create Organisation' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, defineComponent, h, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { useOrgStore } from '@/stores/org'
import { functions } from '@/config/firebase'
import { httpsCallable } from 'firebase/functions'

const router = useRouter()
const authStore = useAuthStore()
const userStore = useUserStore()
const orgStore = useOrgStore()
const isMobileMenuOpen = ref(false)
const isCollapsed = ref(false)
const isReleaseNotesOpen = ref(false)
const isOrgDropdownOpen = ref(false)
const isJoinOrgModalOpen = ref(false)
const isCreateOrgModalOpen = ref(false)
const isJoiningOrg = ref(false)
const isCreatingOrg = ref(false)
const joinOrgError = ref<string | null>(null)
const createOrgError = ref<string | null>(null)
const joinOrgForm = ref({
  orgId: '',
  shareCode: ''
})
const createOrgForm = ref({
  orgName: ''
})
const hasUnreadUpdates = ref(true) // Set to true to show the notification dot

// Watch for release notes being opened while sidebar is collapsed
watch(isReleaseNotesOpen, (newValue) => {
  if (newValue && isCollapsed.value) {
    isCollapsed.value = false
  }
})

// Close release notes when sidebar is collapsed
watch(isCollapsed, (newValue) => {
  if (newValue && isReleaseNotesOpen.value) {
    isReleaseNotesOpen.value = false
  }
  if (newValue && isOrgDropdownOpen.value) {
    isOrgDropdownOpen.value = false
  }
})

// Close org dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.org-switcher-container')) {
    isOrgDropdownOpen.value = false
  }
}

// Start listening to user document changes
onMounted(() => {
  userStore.startListening()
  orgStore.watchOrgData()
  orgStore.fetchUserOrgs()
  document.addEventListener('click', handleClickOutside)
})

// Clean up listener when component unmounts
onUnmounted(() => {
  userStore.stopListening()
  orgStore.cleanup()
  document.removeEventListener('click', handleClickOutside)
})

const handleSwitchOrg = async (orgId: string) => {
  if (orgId === orgStore.orgData?.id) {
    isOrgDropdownOpen.value = false
    return
  }

  try {
    await orgStore.switchOrg(orgId)
    isOrgDropdownOpen.value = false
    // Refresh the page to ensure all stores update with new org context
    window.location.reload()
  } catch (error) {
    console.error('Error switching org:', error)
  }
}

const openJoinOrgModal = () => {
  isOrgDropdownOpen.value = false
  isJoinOrgModalOpen.value = true
  joinOrgError.value = null
  joinOrgForm.value = { orgId: '', shareCode: '' }
}

const closeJoinOrgModal = () => {
  isJoinOrgModalOpen.value = false
  joinOrgError.value = null
  joinOrgForm.value = { orgId: '', shareCode: '' }
}

const handleJoinOrg = async () => {
  if (!joinOrgForm.value.orgId || !joinOrgForm.value.shareCode) {
    joinOrgError.value = 'Please enter both organization ID and share code'
    return
  }

  isJoiningOrg.value = true
  joinOrgError.value = null

  try {
    const joinOrgFn = httpsCallable(functions, 'joinExistingOrg')
    const result = await joinOrgFn({
      org_id: joinOrgForm.value.orgId.trim(),
      share_code: joinOrgForm.value.shareCode.trim()
    })
    const data = result.data as { success: boolean; message?: string }

    if (!data.success) {
      throw new Error(data.message || 'Failed to join organization')
    }

    // Refresh user orgs list
    await orgStore.fetchUserOrgs()
    
    // Close modal and reset form
    closeJoinOrgModal()
    
    // Optionally switch to the newly joined org
    await orgStore.switchOrg(joinOrgForm.value.orgId.trim())
    
    // Refresh the page to ensure all stores update with new org context
    window.location.reload()
  } catch (err: any) {
    joinOrgError.value = err.message || 'Failed to join organization. Please check your organization ID and share code.'
    console.error('Error joining org:', err)
  } finally {
    isJoiningOrg.value = false
  }
}

const openCreateOrgModal = () => {
  isOrgDropdownOpen.value = false
  isCreateOrgModalOpen.value = true
  createOrgError.value = null
  createOrgForm.value = { orgName: '' }
}

const closeCreateOrgModal = () => {
  isCreateOrgModalOpen.value = false
  createOrgError.value = null
  createOrgForm.value = { orgName: '' }
}

const handleCreateOrg = async () => {
  if (!createOrgForm.value.orgName || !createOrgForm.value.orgName.trim()) {
    createOrgError.value = 'Please enter an organisation name'
    return
  }

  isCreatingOrg.value = true
  createOrgError.value = null

  try {
    const createOrgFn = httpsCallable(functions, 'createOrg')
    const result = await createOrgFn({
      org_name: createOrgForm.value.orgName.trim()
    })
    const data = result.data as { success: boolean; org_id?: string; message?: string }

    if (!data.success) {
      throw new Error(data.message || 'Failed to create organization')
    }

    // Refresh user orgs list
    await orgStore.fetchUserOrgs()
    
    // Close modal and reset form
    closeCreateOrgModal()
    
    // Switch to the newly created org
    if (data.org_id) {
      await orgStore.switchOrg(data.org_id)
    }
    
    // Refresh the page to ensure all stores update with new org context
    window.location.reload()
  } catch (err: any) {
    createOrgError.value = err.message || 'Failed to create organization. Please try again.'
    console.error('Error creating org:', err)
  } finally {
    isCreatingOrg.value = false
  }
}

const DashboardIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
    })
  ])
})

const AnalyticsIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
    })
  ])
})

const ProductsIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13'
    })
  ])
})

const WidgetIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z'
    })
  ])
})

const SettingsIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
    }),
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z'
    })
  ])
})

const BusinessIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
    })
  ])
})

const PersonaliseIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M13 10V3L4 14h7v7l9-11h-7z'
    })
  ])
})

// Search icon component
const SearchIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
    })
  ])
})

const FAQIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
    })
  ])
})

// Test tube icon for Searches view
const ExperimentIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z'
    })
  ])
})

const CustomizeIcon = defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    class: 'w-5 h-5'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01'
    })
  ])
})

const navigation = [
  { name: 'Dashboard', href: '/', icon: DashboardIcon },
  { name: 'Analytics', href: '/analytics', icon: AnalyticsIcon },
  { name: 'My Business', href: '/my-business', icon: BusinessIcon },
  { name: 'Test Search', href: '/my-search', icon: ExperimentIcon },
  { name: 'Searches', href: '/searches', icon: SearchIcon },
  { name: 'Product FAQs', href: '/faqs', icon: FAQIcon },
  { name: 'Customize Search', href: '/search-customization', icon: CustomizeIcon },
  { name: 'Personalise', href: '/personalise', icon: PersonaliseIcon },
  { name: 'Integrations', href: '/search-settings', icon: ProductsIcon },
  { name: 'Settings & Billing', href: '/settings', icon: SettingsIcon }
]

const handleSignOut = async () => {
  try {
    await authStore.signOut()
    userStore.stopListening()
    isMobileMenuOpen.value = false
    router.push('/login')
  } catch (error) {
    console.error('Error signing out:', error)
  }
}
</script>